"use client";

import React, { useEffect, useState } from "react";
import { 
  Calendar, 
  User, 
  Clock, 
  Share2, 
  ArrowRight, 
  Tag 
} from "lucide-react";
import { Link } from "@/components/common/Link";
import { 
  Card, 
  CardContent, 
  CardFooter, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";



function RelatedPostsConsumer({ currentPostId }: { currentPostId: string }) {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="2988" className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 bg-muted animate-pulse rounded-md" data-index={i} />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) return null;

  
  const filteredPosts = posts.filter(p => p.id !== currentPostId).slice(0, 3);

  if (filteredPosts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {filteredPosts.map((post, i) => (
        <Card key={post.id} className="flex flex-col h-full border border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 rounded-md" data-index={i}>
          {post.featuredImage && (
            <div className="aspect-[4/3] w-full overflow-hidden rounded-t-md">
              <img 
                src={post.featuredImage} 
                alt={post.title} 
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
          )}
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "Recent"}</span>
            </div>
            <CardTitle className="font-default font-bold text-xl leading-tight line-clamp-2">
              <Link to={post.link || "#"} className="hover:text-secondary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow">
            <div 
              className="text-muted-foreground text-sm line-clamp-3 font-default"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"}>
              <Button variant="link" className="px-0 text-secondary font-bold group">
                View Details 
                <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPosts({ currentPostId, categoryIds }: { currentPostId: string, categoryIds: number[] }) {
  
  const relatedQueryArgs = {
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [currentPostId],
    orderby: "date",
    order: "DESC",
    
    
  };

  const wp_query = new WP_Query(relatedQueryArgs);

  return (
    <div className="mt-16 pt-10 border-t border-border">
      <h3 className="text-2xl font-default font-bold mb-8 text-foreground">Related Insights</h3>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer currentPostId={currentPostId} />
      </PostsProvider>
    </div>
  );
}



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-8 animate-pulse">
        <div className="h-12 bg-muted rounded-md w-3/4 mx-auto" />
        <div className="h-6 bg-muted rounded-md w-1/2 mx-auto" />
        <div className="h-96 bg-muted rounded-md w-full" />
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded-md w-full" />
          <div className="h-4 bg-muted rounded-md w-full" />
          <div className="h-4 bg-muted rounded-md w-5/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-default font-bold text-destructive mb-4">Unable to Load Insight</h2>
        <p className="text-muted-foreground font-default">{error}</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-default font-bold text-foreground mb-4">Insight Not Found</h2>
        <p className="text-muted-foreground font-default">The article you are looking for does not exist.</p>
        <Link to="#">
          <Button className="mt-6 bg-gradient-to-r from-[hsl(330,80%,50%)] to-[hsl(260,80%,50%)] hover:opacity-90 transition-opacity text-white font-bold rounded-md">Back to Overview</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const categoryIds = post.categories?.map(c => c.id) || [];

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-20">
      {}
      <header className="text-center mb-10 md:mb-14">
        <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground mb-6">
          {post.categories && post.categories.length > 0 && (
            <>
              <span className="bg-secondary text-white px-3 py-1 rounded-md font-bold uppercase tracking-wider text-xs">
                {post.categories[0].name}
              </span>
              <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
            </>
          )}
          <div className="flex items-center gap-1.5 font-default">
            <Calendar className="h-4 w-4" />
            <span>{post.date ? new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : ""}</span>
          </div>
          <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <div className="flex items-center gap-1.5 font-default">
            <Clock className="h-4 w-4" />
            <span>4 min read</span>
          </div>
        </div>

        <h1 className="text-3xl md:text-5xl lg:text-6xl font-default font-bold text-foreground leading-tight tracking-tight mb-8">
          {post.title}
        </h1>

        {post.author && (
          <div className="flex items-center justify-center gap-3">
            <Avatar className="h-10 w-10 border border-border shadow-sm">
              <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
              <AvatarFallback><User className="h-5 w-5" /></AvatarFallback>
            </Avatar>
            <div className="text-left font-default">
              <p className="text-sm font-bold text-foreground">By {post.author.name}</p>
              <p className="text-xs text-muted-foreground">Event Operations Lead</p>
            </div>
          </div>
        )}
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 md:mb-16 rounded-md overflow-hidden shadow-lg bg-muted">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-auto object-cover max-h-[600px]"
          />
        </div>
      )}

      {}
      <div className="prose prose-lg prose-slate dark:prose-invert max-w-none mx-auto mb-16 font-default">
        {}
        <div 
          dangerouslySetInnerHTML={{ __html: post.content || "" }} 
          className="[&>p]:mb-6 [&>h2]:text-3xl [&>h2]:font-default [&>h2]:font-bold [&>h2]:mt-10 [&>h2]:mb-4 [&>h3]:text-2xl [&>h3]:font-default [&>h3]:font-bold [&>h3]:mt-8 [&>h3]:mb-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>blockquote]:border-l-4 [&>blockquote]:border-secondary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:my-8 [&>img]:rounded-md [&>img]:my-8"
        />
      </div>

      {}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 py-8 border-t border-b border-border mb-12">
        <div className="flex flex-wrap items-center gap-2">
          <Tag className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-bold mr-2 font-default">Tags:</span>
          {post.tags && post.tags.length > 0 ? (
            post.tags.map((tag, i) => (
              <Link key={tag.id} to={tag.link || "#"}>
                <span className="text-sm text-muted-foreground hover:text-secondary transition-colors bg-muted px-2 py-1 rounded-md font-default" data-index={i}>
                  #{tag.name}
                </span>
              </Link>
            ))
          ) : (
            <span className="text-sm text-muted-foreground font-default">Event Management, AI Insights</span>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold font-default">Share:</span>
          <Button variant="outline" size="icon" className="rounded-md h-8 w-8 border-border hover:bg-muted transition-colors">
            <Share2 className="h-4 w-4" />
          </Button>
          {}
          <Button variant="ghost" size="icon" className="rounded-md h-8 w-8 text-muted-foreground hover:text-[#1877F2]">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" /></svg>
          </Button>
          <Button variant="ghost" size="icon" className="rounded-md h-8 w-8 text-muted-foreground hover:text-[#1DA1F2]">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" /></svg>
          </Button>
        </div>
      </div>

      {}
      {post.author && (
        <div className="bg-card rounded-md p-8 flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left border border-border shadow-sm">
          <Avatar className="h-24 w-24 border-2 border-background shadow-md">
            <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
            <AvatarFallback className="text-2xl"><User /></AvatarFallback>
          </Avatar>
          <div className="flex-1 font-default">
            <h3 className="text-lg font-default font-bold mb-2">About {post.author.name}</h3>
            <p className="text-muted-foreground mb-4">
              {}
              Expert in event commerce and digital ticketing systems. Focused on helping venues leverage AI to optimize fan experiences and operational efficiency through data-driven strategies.
            </p>
            <Link to={post.author.link || "#"}>
              <Button variant="outline" size="sm" className="rounded-md font-bold border-border hover:bg-muted transition-colors">View Author Profile</Button>
            </Link>
          </div>
        </div>
      )}

      {}
      <RelatedPosts currentPostId={post.id} categoryIds={categoryIds} />
    </article>
  );
}

export default function SinglePostTemplate() {
  
  
  const current_wp_query_params = (typeof window !== 'undefined' && (window as any).wvcClient) 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", p: 1 }; 

  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}